import { Fragment } from "react";

import { ErrorMessage } from "common/components";
import { unsafelyKeys } from "common/utilities/object";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import {
  getCurrentCustomMapName,
  getCurrentMap,
  getCurrentMapDimensionIds,
  getLocalImage,
} from "domains/createMapDetail/selectors";
import { interpolate } from "domains/language/interpolate";
import type { Term } from "domains/language/types";
import { activateModal } from "domains/modal/actions";

type CreateMapErrorProps = { isSaved: boolean };

export const CreateMapError = ({ isSaved }: CreateMapErrorProps) => {
  const dispatch = useDispatch();
  const map = useSelector(getCurrentMap);
  const name = useSelector(getCurrentCustomMapName);
  const localImage = useSelector(getLocalImage);
  const dimensionIds = useSelector(getCurrentMapDimensionIds);
  const translate = useTranslate();

  const missingName = !name;
  const missingImage = !map?.images && !localImage;
  const missingDimensions = dimensionIds.length === 0;

  const selectImage = () => dispatch(activateModal({ id: "MapImagePicker" }));

  const errors: Partial<Record<Term, boolean>> = {
    "app.create.create_map.missing_title_image": missingName && missingImage,
    "app.create.create_map.missing_title": missingName && !missingImage,
    "app.create.create_map.missing_image": missingImage && !missingName,
    "app.create.create_map.missing_dimensions": missingDimensions && !missingName && !missingImage,
  };

  const title = (title: string) => <strong>{title}</strong>;
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
  const image = (image: string) => <strong onClick={selectImage}>{image}</strong>;

  const isActive = isSaved && (missingName || missingImage || missingDimensions);

  return (
    <>
      <ErrorMessage isActive={isActive}>
        {unsafelyKeys(errors)
          .filter((key) => errors[key])
          .map((key) => (
            <Fragment key={key}>
              {interpolate(translate(key), {
                title,
                image,
              })}
            </Fragment>
          ))}
      </ErrorMessage>
    </>
  );
};
